import { useQuery } from '@tanstack/react-query';
import { MdDownload } from 'react-icons/md';
import { AxiosError } from 'axios';
import { format, subDays } from 'date-fns';
import prettyBytes from 'pretty-bytes';
import api from '../lib/axios';
import { endpoints } from '../constants/endpoints';
import type { ChatExportInfo } from '../types';

function formatDate(isoString: string): string {
  return format(new Date(isoString), 'd MMM yyyy');
}

function formatDayBefore(isoString: string): string {
  return format(subDays(new Date(isoString), 1), 'd MMM yyyy');
}

export default function ChatExport() {
  const {
    data: exportInfo,
    isLoading,
    error,
  } = useQuery<ChatExportInfo, AxiosError>({
    queryKey: ['chat-export-info'],
    queryFn: () => api.get(endpoints.chat.exportInfo),
    retry: false,
  });

  const isMissing = error?.response?.status === 404;
  const downloadUrl = `${import.meta.env.VITE_BACKEND_URL}${endpoints.chat.exportDownload}`;

  return (
    <div className="flex h-screen flex-col">
      <div className="border-b border-gray-200 bg-white px-6 py-3">
        <h1 className="text-lg font-semibold text-gray-900">Chat Export</h1>
      </div>

      <div className="flex-1 overflow-auto p-6">
        {isLoading && <div className="text-gray-500">Loading...</div>}

        {isMissing && (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="text-base font-medium text-gray-900">
              No export available yet
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              The export regenerates on the 1st of every month. Contact a
              developer if you need one sooner.
            </p>
          </div>
        )}

        {exportInfo && (
          <div className="rounded-lg border border-gray-200 bg-white p-6">
            <h2 className="text-base font-medium text-gray-900">
              Latest export
            </h2>
            <dl className="mt-4 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-gray-500">Generated</dt>
                <dd className="font-medium text-gray-900">
                  {formatDate(exportInfo.generated_at)}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Window</dt>
                <dd className="font-medium text-gray-900">
                  {formatDate(exportInfo.window.start)} →{' '}
                  {formatDayBefore(exportInfo.window.end)}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Sessions</dt>
                <dd className="font-medium text-gray-900">
                  {exportInfo.session_count.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Messages</dt>
                <dd className="font-medium text-gray-900">
                  {exportInfo.message_count.toLocaleString()}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">File size</dt>
                <dd className="font-medium text-gray-900">
                  {prettyBytes(exportInfo.file_size_bytes)}
                </dd>
              </div>
            </dl>

            <a
              href={downloadUrl}
              className="mt-6 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              <MdDownload size={18} />
              Download {exportInfo.download_filename}
            </a>

            <p className="mt-6 text-xs text-gray-500">
              This file contains the last 12 months of chat data in NDJSON
              format. It regenerates automatically on the 1st of every month.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
